/**
 * AccountSegmentCombobox — pole JEDNEGO segmentu konta w formatce dekretacji: zwykły input
 * tekstowy PLUS strzałka rozwijająca listę wartości z planu kont.
 *
 * Wymagania, z których wynika kształt tego komponentu:
 *  - **pole zostaje polem tekstowym** — można wpisać dowolną wartość, także konto, którego
 *    w planie nie ma (backend segmentów nie waliduje, patrz `lib/validators/document-decree.ts`);
 *    lista jest pomocą, nie bramką,
 *  - **szerokość pola się NIE zmienia** — strzałka leży WEWNĄTRZ inputa (absolutnie
 *    pozycjonowana), a nie obok niego; input dostaje tylko ciaśniejszy prawy padding,
 *  - **kaskada** — opcje zależą od segmentów na lewo (`prefixSegments`), patrz
 *    `lib/documents/chart-of-accounts-segments.ts`.
 *
 * Panel renderowany przez `CellPopover` (portal, `fixed`, kotwiczony do pola) — root
 * CLAUDE.md §4.12: lista w komórce tabeli pozycjonowana `absolute` byłaby przycięta przez
 * `overflow-x-auto` siatki dekretu.
 */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { CellPopover } from '@/components/additional/CellPopover';
import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { useDictionary } from '@/hooks/useDictionary';
import {
  type ChartSegmentIndex,
  filterSegmentOptions,
  segmentSource,
  type SymfoniaContractorPosition,
} from '@/lib/documents/chart-of-accounts-segments';

/** Jedna pozycja listy — wspólny kształt dla kont z planu i elementów słownika. */
interface PickOption {
  /** Wartość wstawiana do pola segmentu. */
  value: string;
  /** Główna kolumna listy (numer konta albo pozycja elementu). */
  primary: string;
  /** Opis obok (nazwa konta albo nazwa elementu słownika). */
  secondary: string;
}

export interface AccountSegmentComboboxProps {
  value: string;
  onChange: (value: string) => void;
  /** Etykieta dostępnościowa pola (nazwa kolumny segmentu). */
  ariaLabel: string;
  /** Indeks planu kont wybranego roku; pusty = brak podpowiedzi (strzałka schowana). */
  index: ChartSegmentIndex;
  /** Segmenty NA LEWO od tego pola, w kolejności — sterują kaskadą opcji. */
  prefixSegments: readonly string[];
  /**
   * Pozycje kontrahentów w kartotece Symfonii — dla poziomu analityki oznaczonego w planie
   * kont jako KARTOTEKA KONTRAHENTÓW. Taki poziom nie ma kodu słownika, więc bez tej listy
   * nie ma czym wypełnić podpowiedzi ani podpisać wpisanej liczby. Pusta = brak podpowiedzi
   * (pole nadal przyjmuje dowolną wartość — lista jest pomocą, nie bramką).
   */
  contractorPositions?: readonly SymfoniaContractorPosition[];
  maxLength?: number;
  'data-testid'?: string;
}

export function AccountSegmentCombobox({
  value,
  onChange,
  ariaLabel,
  index,
  prefixSegments,
  contractorPositions,
  maxLength = 30,
  'data-testid': testId,
}: AccountSegmentComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const source = segmentSource(index, prefixSegments);
  const dictionaryLevel = source.kind === 'dictionary' ? source.level : null;
  // Poziom kartotekowy kontrahentów — pozycje przychodzą propem (kartoteka, nie słownik),
  // więc `useDictionary` niżej się dla niego NIE odpala (nie ma kodu słownika).
  const contractorLevel = dictionaryLevel?.source === 'contractors' ? dictionaryLevel : null;
  const contractorOptionsAll: PickOption[] = useMemo(
    () =>
      (contractorPositions ?? []).map((c) => ({
        value: String(c.position),
        primary: String(c.position),
        secondary: c.taxId ? `${c.name} · ${c.taxId}` : c.name,
      })),
    [contractorPositions],
  );

  // Pozycje słownika dociągamy WYŁĄCZNIE dla poziomu słownikowego (hook sam nie strzela
  // bez kodu) — reszta poziomów nie potrzebuje żadnego dodatkowego requestu.
  const dictionaryQ = useDictionary(dictionaryLevel?.dictionaryCode ?? '');

  const options: PickOption[] =
    source.kind === 'accounts'
      ? filterSegmentOptions(source.options, value).map((opt) => ({
          value: opt.value,
          primary: opt.accountNumber,
          secondary: opt.name,
        }))
      : contractorLevel
        ? filterPickOptions(contractorOptionsAll, value)
        : dictionaryLevel
          ? dictionaryItemOptions(dictionaryQ.items, value)
          : [];

  // Strzałka pojawia się także na poziomie słownikowym BEZ pobranych pozycji — panel
  // wyjaśnia wtedy, czego brakuje, zamiast milczeć (user widzi w planie, że poziom jest
  // słownikowy, więc brak strzałki byłby mylący).
  const hasArrow = source.kind === 'accounts' ? source.options.length > 0 : !!dictionaryLevel;

  /**
   * Nazwa tego, co user ma wpisane w polu — konta z planu kont albo elementu słownika na
   * poziomie słownikowym. Pusta, gdy wartości NIE MA w planie (wtedy świadomie nic nie
   * pokazujemy: podpis ma potwierdzać trafienie, nie sugerować, że cokolwiek się znalazło).
   */
  const resolvedName = useMemo(() => {
    const typed = value.trim();
    if (!typed) return '';
    if (source.kind === 'accounts') {
      return source.options.find((opt) => opt.value === typed)?.name ?? '';
    }
    if (contractorLevel) {
      return contractorOptionsAll.find((o) => o.value === typed)?.secondary ?? '';
    }
    if (dictionaryLevel) {
      const item = dictionaryQ.items.find((i) => String(i.sortOrder) === typed);
      return item ? item.label || item.code : '';
    }
    return '';
  }, [value, source, dictionaryLevel, contractorLevel, contractorOptionsAll, dictionaryQ.items]);

  function pick(option: PickOption) {
    onChange(option.value);
    setOpen(false);
  }

  return (
    <div ref={anchorRef}>
      {/* Wewnętrzny `relative` obejmuje TYLKO pole — strzałka jest wyśrodkowana względem
          inputa, nie względem inputa razem z podpisem pod nim (inaczej zjeżdżałaby w dół,
          gdy podpis się pojawi). */}
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          aria-label={ariaLabel}
          // Ciaśniejszy prawy padding niż domyślny `pr-10` z `suffixIcon` — strzałka jest
          // mała, a komórka segmentu wąska. Szerokość pola bez zmian.
          className={hasArrow ? 'pr-7' : undefined}
          data-testid={testId}
        />
        {hasArrow && (
          <IconButton
            size="sm"
            variant="ghost"
            onClick={() => setOpen((v) => !v)}
            aria-label={t('tenant.documents.decree.editor.pickFromChart', { column: ariaLabel })}
            aria-expanded={open}
            className="absolute right-0 top-1/2 h-6 w-6 -translate-y-1/2"
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            <ChevronDown />
          </IconButton>
        )}
      </div>

      {resolvedName && (
        <p
          // Podpis pod polem — mała czcionka, jedna linia z wielokropkiem; pełna nazwa w
          // `title`, bo kolumna segmentu jest wąska. `absolute` NIE wchodzi w grę: podpis
          // musi rozpychać wiersz, inaczej nachodziłby na wiersz poniżej.
          className="mt-0.5 truncate text-[10px] leading-tight text-text-tertiary"
          title={resolvedName}
          data-testid={testId ? `${testId}-name` : undefined}
        >
          {resolvedName}
        </p>
      )}

      <CellPopover
        open={open && hasArrow}
        anchorRef={anchorRef as React.RefObject<HTMLElement | null>}
        onClose={() => setOpen(false)}
        ariaLabel={t('tenant.documents.decree.editor.pickFromChart', { column: ariaLabel })}
        className="max-h-72 w-72 overflow-y-auto p-1"
        data-testid={testId ? `${testId}-panel` : undefined}
      >
        {dictionaryLevel && (
          <p
            className="border-b border-border-primary px-2 pb-2 pt-1 text-xs text-text-tertiary"
            data-testid={testId ? `${testId}-dictionary-header` : undefined}
          >
            {t('tenant.documents.decree.editor.dictionaryLevel', {
              name: dictionaryLevel.name || dictionaryLevel.accountNumber,
            })}
          </p>
        )}
        {options.length === 0 ? (
          <p className="px-2 py-3 text-xs text-text-tertiary">
            {dictionaryLevel
              ? dictionaryQ.isLoading
                ? t('common.loading')
                : dictionaryLevel.dictionaryCode
                  ? t('tenant.documents.decree.editor.noChartMatches')
                  : t('tenant.documents.decree.editor.dictionaryNotLinked')
              : t('tenant.documents.decree.editor.noChartMatches')}
          </p>
        ) : (
          <ul className="space-y-0.5">
            {options.map((option) => (
              <li key={option.primary}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => pick(option)}
                  // `justify-start` nadpisuje wyśrodkowanie z bazowych klas `Button` —
                  // pozycja listy czyta się jak wiersz, nie jak przycisk akcji.
                  className="w-full justify-start gap-2 px-2 text-xs font-normal"
                  data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                >
                  <span className="font-mono tabular-nums text-text-primary">{option.primary}</span>
                  <span className="truncate text-text-secondary">{option.secondary}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CellPopover>
    </div>
  );
}

/**
 * Elementy słownika → pozycje listy. Wartością analityki jest POZYCJA elementu
 * (`sortOrder` = `Position` w FK), więc element bez pozycji nie da się wpisać w konto i
 * jest pomijany. Filtr działa po pozycji (prefiks) i po nazwie elementu.
 */
/** Filtr listy gotowych opcji — po pozycji (prefiks) i po opisie, jak przy słownikach. */
function filterPickOptions(options: readonly PickOption[], query: string): PickOption[] {
  const q = query.trim().toLowerCase();
  return options
    .filter((opt) => !q || opt.primary.startsWith(q) || opt.secondary.toLowerCase().includes(q))
    .sort((a, b) => a.primary.localeCompare(b.primary, undefined, { numeric: true }));
}

function dictionaryItemOptions(
  items: readonly { code: string; label: string; sortOrder: number | null; isActive: boolean }[],
  query: string,
): PickOption[] {
  const q = query.trim().toLowerCase();
  return items
    .filter((item) => item.isActive && item.sortOrder !== null)
    .map((item) => ({
      value: String(item.sortOrder),
      primary: String(item.sortOrder),
      secondary: item.label || item.code,
    }))
    .filter((opt) => !q || opt.primary.startsWith(q) || opt.secondary.toLowerCase().includes(q))
    .sort((a, b) => a.primary.localeCompare(b.primary, undefined, { numeric: true }));
}
